/**
 * The finish — a photo strip printing out of the booth.
 *
 * Creating a booth used to be a route change: the creator finished designing
 * something and got a page transition. This is the one screenshot-worthy
 * second in the flow, and the strip is the object this whole product exists to
 * produce, so the booth prints one: the welcome screen, the palette, the URL
 * and a QR to it, sliding out and settling.
 *
 * Deliberately not a real capture — there are no photos yet. It's the booth's
 * own design on strip stock, which is exactly what the creator would post.
 */

import React, { useEffect, useRef, useState } from 'react'
import { FaCheck, FaCopy, FaArrowRight } from 'react-icons/fa'
import QRCode from 'qrcode'
import type { WelcomeComponentDto } from '../onboardingApi'
import WelcomePreview from './WelcomePreview'
import { useCopy } from '../regionCopy'
import { publicBoothUrl, publicBoothHostLabel } from '../publicOrigin'

/** Everything the strip needs, captured before the draft is discarded. */
export interface GoLiveSnapshot {
  slug: string
  title: string
  components: WelcomeComponentDto[]
  palette: { backgroundColor: string; primaryColor: string; secondaryColor: string }
}

interface GoLiveStripProps extends GoLiveSnapshot {
  onOpen: () => void
}

const STRIP_PHOTO_WIDTH = 152

export const GoLiveStrip: React.FC<GoLiveStripProps> = ({
  slug,
  title,
  components,
  palette,
  onOpen
}) => {
  const url = publicBoothUrl(slug)
  const [qr, setQr] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const txt = useCopy()
  const [printed, setPrinted] = useState(false)
  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    let cancelled = false
    void QRCode.toDataURL(url, { margin: 0, width: 240, color: { light: '#0000' } })
      .then((data) => {
        if (!cancelled) setQr(data)
      })
      .catch(() => undefined)
    // One frame's delay so the strip animates in rather than appearing done.
    const t = setTimeout(() => setPrinted(true), 80)
    return () => {
      cancelled = true
      clearTimeout(t)
    }
  }, [url])

  useEffect(
    () => () => {
      if (copyTimer.current) clearTimeout(copyTimer.current)
    },
    []
  )

  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      if (copyTimer.current) clearTimeout(copyTimer.current)
      copyTimer.current = setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard blocked (insecure context / permission) — the URL is on
      // screen and selectable, so this is a silent downgrade, not an error.
    }
  }

  return (
    <div className="mx-auto flex w-full max-w-xl flex-col items-center gap-8 px-6 py-12 text-center">
      <div className="space-y-2">
        <h2 className="text-2xl font-bold sm:text-3xl">
          <span className="bg-gradient-to-r from-purpleSnail to-luridRed bg-clip-text text-transparent">
            {title}
          </span>{' '}
          is live
        </h2>
        <p className="text-sm text-whiteout/55">
          Share the link. Anyone who opens it gets your booth — no app, no install.
        </p>
      </div>

      {/* The strip */}
      <div
        className="overflow-hidden rounded-lg shadow-2xl ring-1 ring-black/30 transition-all duration-700 ease-out motion-reduce:transition-none"
        style={{
          width: STRIP_PHOTO_WIDTH + 16,
          backgroundColor: palette.secondaryColor || '#FFFFFF',
          opacity: printed ? 1 : 0,
          transform: printed ? 'translateY(0)' : 'translateY(-2.5rem)'
        }}
      >
        <div className="p-2">
          {/* The creator's actual welcome screen, not a stand-in. */}
          <WelcomePreview
            components={components}
            orientation="portrait"
            backgroundColor={palette.backgroundColor}
            displayWidth={STRIP_PHOTO_WIDTH}
          />
        </div>

        {/* Strip footer — where a real strip carries its branding */}
        <div className="flex items-center gap-2 px-3 pb-3 pt-1">
          {qr && (
            <img
              src={qr}
              alt=""
              aria-hidden
              className="h-9 w-9 shrink-0"
              style={{ filter: 'invert(1)' }}
            />
          )}
          <div className="min-w-0 text-left">
            <div
              className="truncate font-mono text-[8px] leading-tight"
              style={{ color: palette.primaryColor }}
            >
              {publicBoothHostLabel()}/
            </div>
            <div
              className="truncate font-mono text-[9px] font-semibold leading-tight"
              style={{ color: palette.primaryColor }}
            >
              {slug}
            </div>
          </div>
        </div>
      </div>

      <div className="flex w-full max-w-sm flex-col items-stretch gap-3 sm:flex-row sm:justify-center">
        <button
          type="button"
          onClick={() => void copy()}
          className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm font-medium text-whiteout/80 transition-colors hover:border-white/35 hover:text-whiteout"
        >
          {copied ? <FaCheck className="text-green-400" /> : <FaCopy className="text-xs" />}
          {copied ? txt.copied : txt.copyLink}
        </button>
        <button
          type="button"
          onClick={onOpen}
          className="inline-flex items-center justify-center gap-2 rounded-full bg-blueSparkle px-7 py-2.5 text-sm font-semibold text-whiteout shadow-lg transition-opacity hover:opacity-90"
        >
          {txt.openMyBooth} <FaArrowRight className="text-xs" />
        </button>
      </div>
    </div>
  )
}

export default GoLiveStrip
